/**
 * `UmssRadioFieldsetComponent` — Container that manages mutual exclusion
 * across projected radio children. Implements ControlValueAccessor at the
 * group level so you bind a single `[formControl]` on the fieldset.
 *
 * Accepts any child that provides `UMSS_RADIO_ITEM`:
 * - `<umss-radio>`
 * - `<umss-radio-group>`
 * - `<umss-rich-radio-group>`
 *
 * @example
 * <!-- Basic usage with FormControl -->
 * <umss-radio-fieldset label="Rehire Status" [formControl]="rehireControl">
 *   <umss-radio-group value="rehirable" label="Rehirable" helperText="Would be considered for rehire" />
 *   <umss-radio-group value="not-rehirable" label="NOT Rehirable" helperText="Would NOT be considered" />
 * </umss-radio-fieldset>
 *
 * @example
 * <!-- Rich radio items with horizontal layout -->
 * <umss-radio-fieldset label="Plan" [formControl]="planControl" layout="Horizontal">
 *   <umss-rich-radio-group value="basic" label="Basic" secondaryText="Free forever" />
 *   <umss-rich-radio-group value="pro" label="Pro" secondaryText="$9/mo" />
 * </umss-radio-fieldset>
 *
 * @example
 * <!-- Mixing radio types -->
 * <umss-radio-fieldset [formControl]="ctrl" layout="Vertical">
 *   <umss-radio-group value="simple" label="Simple option" />
 *   <umss-rich-radio-group value="rich" label="Rich option" secondaryText="With description" />
 * </umss-radio-fieldset>
 *
 * @example
 * <!-- With ngModel -->
 * <umss-radio-fieldset label="Size" [(ngModel)]="selectedSize" name="size">
 *   <umss-radio-group value="S" label="Small" />
 *   <umss-radio-group value="M" label="Medium" />
 *   <umss-radio-group value="L" label="Large" />
 * </umss-radio-fieldset>
 */
/* eslint-disable @typescript-eslint/no-empty-function */
import { CommonModule } from '@angular/common';
import {
  AfterContentInit,
  Component,
  ContentChildren,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnDestroy,
  Output,
  QueryList
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { UmssLabelComponent } from '../label/label.component';
import { UMSS_RADIO_ITEM, UmssRadioItem } from './radio-item.token';

@Component({
  selector: 'umss-radio-fieldset',
  standalone: true,
  imports: [CommonModule, UmssLabelComponent],
  templateUrl: './radio-fieldset.component.html',
  styleUrls: ['./radio-fieldset.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssRadioFieldsetComponent),
      multi: true
    }
  ]
})
export class UmssRadioFieldsetComponent
  implements ControlValueAccessor, AfterContentInit, OnDestroy
{
  @HostBinding('style.display') display = 'contents';

  @ContentChildren(UMSS_RADIO_ITEM, { descendants: true })
  radioItems!: QueryList<UmssRadioItem>;

  @Input() label?: string;
  @Input() helperText?: string;
  @Input() name?: string;
  @Input() layout: 'Vertical' | 'Horizontal' = 'Vertical';
  @Input() size: 'Small' | 'Regular' | 'Large' = 'Regular';
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() error: boolean = false;

  @Output() valueChange = new EventEmitter<unknown>();

  private _value: unknown = null;
  private _onChange: (value: unknown) => void = () => {};
  private _onTouched: () => void = () => {};
  private _itemSubscriptions: Subscription[] = [];
  private _changesSubscription?: Subscription;

  writeValue(value: unknown): void {
    this._value = value;
    this._syncChildren();
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this._propagateDisabled();
  }

  ngAfterContentInit(): void {
    this._changesSubscription = this.radioItems.changes
      .pipe(startWith(this.radioItems))
      .subscribe(() => this._setupItems());
  }

  ngOnDestroy(): void {
    this._cleanupSubscriptions();
    this._changesSubscription?.unsubscribe();
  }

  private _setupItems(): void {
    this._cleanupSubscriptions();
    this._propagateName();
    this._propagateDisabled();
    this._syncChildren();
    this._subscribeToChanges();
  }

  private _subscribeToChanges(): void {
    this.radioItems.forEach((item) => {
      const sub = item.checkedChange.subscribe((selectedValue: unknown) => {
        this._value = selectedValue;
        this._syncChildren();
        this._onChange(selectedValue);
        this._onTouched();
        this.valueChange.emit(selectedValue);
      });
      this._itemSubscriptions.push(sub);
    });
  }

  private _syncChildren(): void {
    if (!this.radioItems) return;
    this.radioItems.forEach((item) => {
      item.checked = item.value === this._value;
    });
  }

  private _propagateDisabled(): void {
    if (!this.radioItems) return;
    this.radioItems.forEach((item) => {
      item.disabled = this.disabled;
    });
  }

  private _propagateName(): void {
    if (!this.radioItems || !this.name) return;
    this.radioItems.forEach((item) => {
      item.name = this.name;
    });
  }

  private _cleanupSubscriptions(): void {
    this._itemSubscriptions.forEach((s) => s.unsubscribe());
    this._itemSubscriptions = [];
  }
}
